<script setup lang="ts">
import { ref } from "vue";
import { useAuth } from "../composables/useAuth.js";
import { useI18n } from "../composables/useI18n.js";

const { isAuthenticated, emailVerified, resendVerification } = useAuth();
const { t } = useI18n();

const sending = ref(false);
const message = ref("");
const error = ref("");

async function resend(): Promise<void> {
  sending.value = true;
  message.value = "";
  error.value = "";
  try {
    message.value = await resendVerification();
  } catch (err) {
    error.value = err instanceof Error ? err.message : t("Could not send the verification email. Please try again.");
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <div v-if="isAuthenticated && !emailVerified" class="verify-banner" role="status">
    <span class="verify-banner__text">
      {{ t('Please confirm your email address to unlock community features (posting in the forum).') }}
    </span>
    <span v-if="message" class="verify-banner__ok">{{ message }}</span>
    <span v-else-if="error" class="verify-banner__err">{{ error }}</span>
    <button v-else class="verify-banner__btn" type="button" :disabled="sending" @click="resend">
      {{ sending ? t('Sending...') : t('Resend verification email') }}
    </button>
  </div>
</template>

<style scoped>
.verify-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  padding: 10px 16px;
  background: color-mix(in srgb, var(--accent) 14%, var(--surface));
  border-bottom: 1px solid var(--border);
  color: var(--text);
  font-size: 0.9rem;
}
.verify-banner__text { flex: 1 1 auto; }
.verify-banner__ok { color: var(--green); font-weight: 500; }
.verify-banner__err { color: var(--danger-text); font-weight: 500; }
.verify-banner__btn {
  flex: 0 0 auto;
  padding: 6px 14px;
  color: white;
  background: var(--green);
  border: 0;
  border-radius: var(--radius-sm);
  font-weight: 600;
  cursor: pointer;
}
.verify-banner__btn:disabled { opacity: 0.55; cursor: not-allowed; }
</style>
